import { useState } from 'react';
import { CreditCard, Loader, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { getStripe, calculatePlatformFee, PLATFORM_FEE_PERCENT } from './stripe';
import SquarePaymentModal from './SquarePaymentModal';

export default function PayInvoiceButton({
  invoice,
  companyId,
  companyStripeAccountId,
  companySquareMerchantId,
  companySquareLocationId,
  companyName,
  onPaymentSuccess,
  size = 'normal', // 'normal' or 'large'
  showFinancingOption = true // Show financing available message
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSquareModal, setShowSquareModal] = useState(false);
  const [showProcessorChoice, setShowProcessorChoice] = useState(false);

  const hasStripe = !!companyStripeAccountId;
  const hasSquare = !!companySquareMerchantId && !!companySquareLocationId;

  const handlePayment = async () => {
    if (!hasStripe && !hasSquare) {
      alert('This company has not set up online payments yet. Please contact them directly.');
      return;
    }

    // Both connected — let user pick
    if (hasStripe && hasSquare) {
      setShowProcessorChoice(true);
      return;
    }

    // Only Square connected
    if (hasSquare && !hasStripe) {
      setShowSquareModal(true);
      return;
    }

    // Stripe-only path (original)
    return handleStripePayment();
  };

  const handleStripePayment = async () => {

    setLoading(true);
    setError(null);

    try {
      // Calculate amounts
      // Charge the remaining balance, not the full total — an invoice may
      // already have a deposit or partial payment recorded in amount_paid.
      const amountDue = Math.max(0, (invoice.total || 0) - (invoice.amount_paid || 0));
      const amountCents = Math.round(amountDue * 100);
      const platformFeeCents = calculatePlatformFee(amountCents);

      // Create checkout session via API
      const stripeUrl = await createCheckoutSession({
        invoiceId: invoice.id,
        amount: amountCents,
        platformFee: platformFeeCents,
        connectedAccountId: companyStripeAccountId,
        customerEmail: invoice.customer_email,
        description: `Invoice #${invoice.invoice_number}`,
        companyName: companyName
      });

      if (stripeUrl) {
        // Redirect to Stripe Checkout
        window.location.href = stripeUrl;
      }

    } catch (err) {
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Call backend API to create Stripe Checkout Session for connected account
  const createCheckoutSession = async ({ invoiceId, amount, platformFee, connectedAccountId, customerEmail, description, companyName }) => {
    // Determine if BNPL should be enabled (for amounts $1-$4000)
    const enableBnpl = amount >= 100 && amount <= 400000;

    const response = await fetch('/api/connect-checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        invoiceId,
        amount,
        platformFee,
        connectedAccountId,
        customerEmail,
        description,
        companyName,
        enableBnpl
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create checkout session');
    }

    return data.url;
  };

  // Check if financing would be available for this amount
  const amountCents = Math.round(Math.max(0, (invoice.total || 0) - (invoice.amount_paid || 0)) * 100);
  const financingAvailable = amountCents >= 100 && amountCents <= 400000;

  const isLarge = size === 'large';

  if (invoice.status === 'paid') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: isLarge ? '1rem 2rem' : '0.5rem 1rem',
        background: '#4ecca320',
        borderRadius: '8px',
        color: '#4ecca3',
        fontWeight: 'bold',
        fontSize: isLarge ? '1.1rem' : '0.9rem'
      }}>
        <CheckCircle size={isLarge ? 20 : 16} />
        Paid
      </div>
    );
  }

  const noProcessor = !hasStripe && !hasSquare;
  const buttonBg = hasStripe ? '#635bff' : '#006AFF'; // Stripe purple or Square blue

  return (
    <div>
      <button
        onClick={handlePayment}
        disabled={loading || noProcessor}
        style={{
          padding: isLarge ? '1rem 2rem' : '0.75rem 1.5rem',
          background: loading ? '#3a3a3a' : buttonBg,
          border: 'none',
          borderRadius: '8px',
          color: '#fff',
          fontWeight: 'bold',
          cursor: loading || noProcessor ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: isLarge ? '1.1rem' : '0.9rem',
          opacity: noProcessor ? 0.5 : 1
        }}
      >
        {loading ? (
          <>
            <Loader size={isLarge ? 20 : 16} style={{ animation: 'spin 1s linear infinite' }} />
            Processing...
          </>
        ) : (
          <>
            <CreditCard size={isLarge ? 20 : 16} />
            Pay ${(Math.max(0, (invoice.total || 0) - (invoice.amount_paid || 0))).toFixed(2)}
          </>
        )}
      </button>

      {/* Processing fee notice */}
      {invoice.processing_fee > 0 && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.4rem 0.75rem',
          background: '#f39c1215',
          borderRadius: '6px',
          fontSize: '0.75rem',
          color: '#f39c12',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>Includes card processing fee</span>
          <span style={{ fontWeight: 'bold' }}>${invoice.processing_fee?.toFixed(2)}</span>
        </div>
      )}

      {/* Financing available message — Stripe only (BNPL via Afterpay/Klarna/Affirm) */}
      {showFinancingOption && financingAvailable && hasStripe && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.5rem 0.75rem',
          background: 'linear-gradient(135deg, #8b5cf620, #6366f120)',
          borderRadius: '6px',
          fontSize: '0.8rem',
          color: '#a78bfa',
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem'
        }}>
          <Sparkles size={14} />
          Financing available with Afterpay, Klarna, or Affirm
        </div>
      )}

      {noProcessor && (
        <div style={{
          marginTop: '0.5rem',
          fontSize: '0.8rem',
          color: '#888'
        }}>
          Online payments not available
        </div>
      )}

      {error && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.5rem',
          background: '#e74c3c20',
          borderRadius: '4px',
          color: '#e74c3c',
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      {/* Processor choice modal — when both Stripe and Square are connected */}
      {showProcessorChoice && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
        }}>
          <div style={{
            background: '#1a1a1a', borderRadius: '12px', padding: '1.5rem',
            maxWidth: '380px', width: '100%', border: '1px solid #3a3a3a',
          }}>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: '1.05rem', marginBottom: '0.25rem' }}>
              Choose payment method
            </div>
            <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
              Both Stripe and Square are connected for {companyName || 'this company'}.
            </div>

            <button
              onClick={() => { setShowProcessorChoice(false); handleStripePayment(); }}
              style={{
                width: '100%', padding: '0.85rem', marginBottom: '0.5rem',
                background: '#635bff', border: 'none', borderRadius: '8px',
                color: '#fff', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              }}
            >
              Pay with Stripe
              {financingAvailable && (
                <span style={{ fontSize: '0.7rem', opacity: 0.9 }}>(Financing available)</span>
              )}
            </button>
            <button
              onClick={() => { setShowProcessorChoice(false); setShowSquareModal(true); }}
              style={{
                width: '100%', padding: '0.85rem', marginBottom: '0.75rem',
                background: '#006AFF', border: 'none', borderRadius: '8px',
                color: '#fff', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Pay with Square
            </button>
            <button
              onClick={() => setShowProcessorChoice(false)}
              style={{
                width: '100%', padding: '0.6rem', background: 'transparent',
                border: '1px solid #3a3a3a', borderRadius: '8px',
                color: '#888', fontWeight: 500, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showSquareModal && (
        <SquarePaymentModal
          invoice={invoice}
          companyId={companyId}
          squareLocationId={companySquareLocationId}
          companyName={companyName}
          onClose={() => setShowSquareModal(false)}
          onPaymentSuccess={(payment) => {
            if (onPaymentSuccess) onPaymentSuccess(payment);
          }}
        />
      )}
    </div>
  );
}

// Customer-facing invoice payment page component
export function InvoicePaymentPage({ invoiceId }) {
  const [invoice, setInvoice] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useState(() => {
    loadInvoice();
  }, [invoiceId]);

  const loadInvoice = async () => {
    try {
      // Load invoice with company info
      const { data, error } = await supabaseData
        .from('invoices')
        .select(`
          *,
          companies:company_id (
            name,
            stripe_connect_id,
            logo_url
          ),
          customers:customer_id (
            name,
            email
          )
        `)
        .eq('id', invoiceId)
        .single();

      if (error) throw error;

      setInvoice({
        ...data,
        customer_email: data.customers?.email
      });
      setCompany(data.companies);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0a0a0a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#888'
      }}>
        Loading invoice...
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0a0a0a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#e74c3c'
      }}>
        Invoice not found
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      padding: '2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        background: '#1a1a1a',
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: '500px',
        width: '100%',
        border: '1px solid #3a3a3a'
      }}>
        {/* Company Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid #3a3a3a'
        }}>
          {company?.logo_url ? (
            <img
              src={company.logo_url}
              alt={company.name}
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '12px',
                objectFit: 'contain',
                marginBottom: '1rem'
              }}
            />
          ) : (
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '12px',
              background: '#2a2a2a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '2rem'
            }}>
              🚪
            </div>
          )}
          <h2 style={{ margin: 0, color: '#e0e0e0' }}>
            {company?.name || 'Invoice'}
          </h2>
        </div>

        {/* Invoice Details */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.5rem',
            color: '#888'
          }}>
            <span>Invoice #</span>
            <span style={{ color: '#e0e0e0' }}>{invoice.invoice_number}</span>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.5rem',
            color: '#888'
          }}>
            <span>Customer</span>
            <span style={{ color: '#e0e0e0' }}>{invoice.customers?.name}</span>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.5rem',
            color: '#888'
          }}>
            <span>Due Date</span>
            <span style={{ color: '#e0e0e0' }}>
              {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'Upon receipt'}
            </span>
          </div>
        </div>

        {/* Amount */}
        <div style={{
          background: '#2a2a2a',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          {/* Show breakdown if there's a processing fee */}
          {invoice.processing_fee > 0 ? (
            <>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem',
                color: '#888',
                fontSize: '0.9rem'
              }}>
                <span>Subtotal</span>
                <span style={{ color: '#e0e0e0' }}>
                  ${((invoice.total || 0) - (invoice.processing_fee || 0)).toFixed(2)}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.75rem',
                paddingBottom: '0.75rem',
                borderBottom: '1px solid #3a3a3a',
                color: '#f39c12',
                fontSize: '0.9rem'
              }}>
                <span>Card Processing Fee</span>
                <span>${invoice.processing_fee?.toFixed(2)}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ color: '#888' }}>Amount Due</span>
                <span style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#4ecca3'
                }}>
                  ${(Math.max(0, (invoice.total || 0) - (invoice.amount_paid || 0))).toFixed(2)}
                </span>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#888', marginBottom: '0.5rem' }}>Amount Due</div>
              <div style={{
                fontSize: '2.5rem',
                fontWeight: 'bold',
                color: '#4ecca3'
              }}>
                ${(Math.max(0, (invoice.total || 0) - (invoice.amount_paid || 0))).toFixed(2)}
              </div>
            </div>
          )}
        </div>

        {/* Pay Button */}
        <PayInvoiceButton
          invoice={invoice}
          companyStripeAccountId={company?.stripe_connect_id}
          companyName={company?.name}
          size="large"
        />

        {/* Secure Payment Notice */}
        <div style={{
          marginTop: '1.5rem',
          textAlign: 'center',
          color: '#666',
          fontSize: '0.8rem'
        }}>
          🔒 Secure payment powered by Stripe
        </div>
      </div>
    </div>
  );
}
